import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useState } from 'react'
import { AGENT_BEHAVIOR_KEYS, AGENT_BEHAVIOR_LABELS, agentBehaviors, type AdminSettingKey } from '@afra/shared'
import { RequestError, api } from '../../lib/api'

/**
 * Policy Afra can change without a deploy.
 *
 * Deliberately short, and meant to stay that way. A setting belongs here only
 * when it is a decision about what charities experience; anything
 * environment-shaped lives in `.env`, where a deploy is the right ceremony.
 */
export function SettingsTab({ canManage, canManageBehaviors = canManage }: { canManage: boolean; canManageBehaviors?: boolean }) {
  const [error, setError] = useState<string | null>(null)
  const queryClient = useQueryClient()

  const settings = useQuery({ queryKey: ['admin-settings'], queryFn: api.adminSettings })

  const save = useMutation({
    mutationFn: ({ key, value }: { key: AdminSettingKey; value: string }) =>
      api.adminSetSetting(key, value),
    onSuccess: (result) => {
      setError(null)
      queryClient.setQueryData(['admin-settings'], { settings: result.settings })
    },
    onError: (err) =>
      setError(err instanceof RequestError ? err.messageFa : 'تنظیم ذخیره نشد.'),
  })

  const showToolCalls = settings.data?.settings.show_tool_calls !== 'false'
  const behaviors = agentBehaviors(settings.data?.settings ?? {})

  return (
    <div className="stack">
      {error && <p className="notice notice--error">{error}</p>}
      {settings.isError && <p className="notice notice--error">دریافت تنظیمات انجام نشد. <button type="button" className="btn btn--quiet btn--sm" onClick={() => void settings.refetch()}>تلاش دوباره</button></p>}

      <div className="card">
        <h2>تنظیمات</h2>

        {settings.isLoading && <p className="muted">در حال بارگذاری…</p>}

        {settings.data && (
          <div className="stack">
            <div>
              <div className="row" style={{ justifyContent: 'space-between' }}>
                <div style={{ maxWidth: '44rem' }}>
                  <div style={{ fontWeight: 700 }}>نمایش کارهای افرا در گفت‌وگو</div>
                  <p className="meta" style={{ marginBottom: 0 }}>
                    وقتی روشن است، مؤسسه هر کاری را که افرا وسط گفت‌وگو انجام می‌دهد می‌بیند: چه
                    چیزی را جست‌وجو کرد، چه چیزی را ثبت کرد. پیشنهاد ما روشن نگه داشتن آن است —
                    دستیاری که در پرونده مؤسسه می‌نویسد باید جلوی چشم بنویسد. کارشناسان افرا در
                    هر صورت این‌ها را می‌بینند.
                  </p>
                </div>

                <div className="row">
                  <span className={`badge ${showToolCalls ? 'badge--good' : 'badge--stored'}`}>
                    {showToolCalls ? 'روشن' : 'خاموش'}
                  </span>
                  {canManage && (
                    <button
                      type="button"
                      className="btn btn--ghost btn--sm"
                      disabled={save.isPending}
                      onClick={() =>
                        save.mutate({
                          key: 'show_tool_calls',
                          value: showToolCalls ? 'false' : 'true',
                        })
                      }
                    >
                      {showToolCalls ? 'خاموش کن' : 'روشن کن'}
                    </button>
                  )}
                </div>
              </div>
            </div>

            {!canManage && (
              <p className="meta">تغییر این تنظیم‌ها از عهدهٔ مدیر ارشد برمی‌آید.</p>
            )}
          </div>
        )}
      </div>
      <div className="card">
        <h2>مقایسه رفتارهای افرا</h2>
        <p className="meta">هر تغییر از پاسخ بعدی در همه مؤسسه‌ها اعمال می‌شود. برای مقایسه بهتر، هر بار یک رفتار را تغییر دهید و گفت‌وگوی تازه‌ای با مسئله مشابه آغاز کنید. قواعد شواهد و ثبت اقدام اصلاحی همیشه فعال‌اند.</p>
        {settings.isLoading && <p className="muted">در حال بارگذاری…</p>}
        {settings.data && <div className="stack">
          {AGENT_BEHAVIOR_KEYS.map((key) => <div className="row" key={key} style={{ justifyContent: 'space-between', alignItems: 'flex-start' }}>
            <div style={{ maxWidth: '44rem', flex: '1 1 18rem' }}>
              <h3 id={key}>{AGENT_BEHAVIOR_LABELS[key].title}</h3>
              <p className="meta" id={`${key}-description`}>{AGENT_BEHAVIOR_LABELS[key].description}</p>
            </div>
            <button type="button" role="switch" aria-checked={behaviors[key]} aria-labelledby={key} aria-describedby={`${key}-description`}
              className="btn btn--ghost btn--sm" disabled={!canManageBehaviors || save.isPending}
              onClick={() => save.mutate({ key, value: String(!behaviors[key]) })}>
              {behaviors[key] ? 'روشن' : 'خاموش'}
            </button>
          </div>)}
          <p className="meta" role="status">{save.isPending ? 'در حال ذخیره…' : save.isSuccess ? 'تنظیم ذخیره شد و از پاسخ بعدی اعمال می‌شود.' : 'هر چهار رفتار به‌صورت پیش‌فرض روشن‌اند.'}</p>
        </div>}
      </div>
    </div>
  )
}
